import React, {Component} from 'react'
import {
  Box,
  Button,
  Heading,
  Text,
  VStack,
  Grid,
  GridItem,
  Card,
  CardHeader,
  CardBody,
  HStack,
  FormControl,
  FormLabel,
  NumberInput,
  NumberInputField,
  NumberInputStepper,
  NumberIncrementStepper,
  NumberDecrementStepper,
  Alert,
  AlertIcon,
  AlertTitle,
  AlertDescription,
  Divider,
  Stack,
  Switch,
  Modal,
  ModalOverlay,
  ModalContent,
  ModalHeader,
  ModalFooter,
  ModalBody,
  ModalCloseButton,
  Input,
  Spinner,
  Badge
} from '@chakra-ui/react';
import { CheckIcon, SettingsIcon, DownloadIcon, DeleteIcon, WarningIcon, LockIcon } from '@chakra-ui/icons';
import './Instellingen.css'
import Sidebar from '../Sidebar/Sidebar';
import Navigation from '../../Navigation/Navigation';
import MobileMenu from '../MobileMenu/MobileMenu';
import Footer from '../../../components/Footer/Footer';
import colors from '../../../theme/colors';

// API URL from environment variable with fallback to localhost
const API_URL = process.env.REACT_APP_API_URL || '';

class Instellingen extends Component {
  constructor(props) {
    super(props);
    this.state = {
      isMobileMenuOpen: false,
      settings: {
        minimumPersonsForTrending: 2,
        minimumSuggestionsForTrending: 3
      },
      originalSettings: {
        minimumPersonsForTrending: 2,
        minimumSuggestionsForTrending: 3
      },
      hasChanges: false,
      saving: false,
      // Privacy settings for Burger users
      privacySettings: {
        allowSuggestionSummarization: false
      },
      originalPrivacySettings: {
        allowSuggestionSummarization: false
      },
      hasPrivacyChanges: false,
      savingPrivacy: false,
      // Data export
      exportLoading: false,
      exportStatus: null,
      // Account deletion
      showDeleteModal: false,
      deleteConfirmation: '',
      deletingAccount: false,
      // Password change
      currentPassword: '',
      newPassword: '',
      confirmPassword: '',
      changingPassword: false,
      // Email change
      newEmail: '',
      changingEmail: false
    };
  }

  componentDidMount() {
    // Only load trending settings for Vertegenwoordiger users
    if (this.props.state.userVersion === 'vertegenwoordiger') {
      this.loadSettings();
    }
    // Load privacy settings for Burger users
    if (this.props.state.userVersion === 'burger') {
      this.loadPrivacySettings();
    }
  }

  loadSettings = async () => {
    try {
      const response = await fetch(`${API_URL}/get_trending_settings`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        },
        credentials: 'include'
      });
      
      const data = await response.json();
      
      if (data.success) {
        this.setState({
          settings: data.result,
          originalSettings: { ...data.result }
        });
      } else {
        console.error('Error loading trending settings:', data.message);
        this.loadSettingsFromLocalStorage();
      }
    } catch (error) {
      console.error('Error fetching trending settings:', error);
      this.loadSettingsFromLocalStorage();
    }
  }

  loadSettingsFromLocalStorage = () => {
    const savedSettings = localStorage.getItem('trendingSettings');
    if (savedSettings) {
      try {
        const parsedSettings = JSON.parse(savedSettings);
        this.setState({
          settings: parsedSettings,
          originalSettings: { ...parsedSettings }
        });
      } catch (e) {
        console.error('Error loading trending settings from localStorage:', e);
      }
    }
  }

  handleSettingChange = (settingName, value) => {
    const newSettings = {
      ...this.state.settings,
      [settingName]: value
    };
    
    const hasChanges = JSON.stringify(newSettings) !== JSON.stringify(this.state.originalSettings);
    
    this.setState({
      settings: newSettings,
      hasChanges
    });
  }

  saveSettings = async () => {
    this.setState({ saving: true });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/update_trending_settings`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(this.state.settings)
      });

      const data = await response.json();
      
      if (data.success) {
        localStorage.setItem('trendingSettings', JSON.stringify(this.state.settings));
        
        this.setState({
          originalSettings: { ...this.state.settings },
          hasChanges: false,
          saving: false
        });
        
        if (this.props.showToast) {
          this.props.showToast({
            title: "Instellingen opgeslagen",
            position: 'top',
            description: "De trending issues instellingen zijn bijgewerkt.",
            status: "success",
            duration: 3000,
            isClosable: true
          });
        }
      } else {
        throw new Error(data.message || 'Unknown server error');
      }
      
    } catch (error) {
      console.error('Error saving trending settings:', error);
      this.setState({ saving: false });
      
      if (this.props.showToast) {
        this.props.showToast({
          title: "Fout bij opslaan",
          position: 'top',
          description: error.message || "Er is een fout opgetreden bij het opslaan van de instellingen.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    }
  }

  resetSettings = () => {
    const defaultSettings = {
      minimumPersonsForTrending: 2,
      minimumSuggestionsForTrending: 3
    };

    this.setState({
      settings: defaultSettings,
      hasChanges: JSON.stringify(defaultSettings) !== JSON.stringify(this.state.originalSettings)
    });
  }

  // Privacy settings methods
  loadPrivacySettings = async () => {
    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/privacy/settings/${this.props.state.username}`, {
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
        }
      });

      const data = await response.json();

      if (data.success || response.ok) {
        const settings = {
          allowSuggestionSummarization: data.allow_suggestion_summarization !== false
        };

        this.setState({
          privacySettings: settings,
          originalPrivacySettings: { ...settings }
        });
      }
    } catch (error) {
      console.error('Error fetching privacy settings:', error);
    }
  }

  handlePrivacySettingChange = (settingName, value) => {
    const newPrivacySettings = {
      ...this.state.privacySettings,
      [settingName]: value
    };

    const hasPrivacyChanges = JSON.stringify(newPrivacySettings) !== JSON.stringify(this.state.originalPrivacySettings);

    this.setState({
      privacySettings: newPrivacySettings,
      hasPrivacyChanges
    });
  }

  savePrivacySettings = async () => {
    this.setState({ savingPrivacy: true });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/privacy/settings`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: this.props.state.username,
          allow_suggestion_summarization: this.state.privacySettings.allowSuggestionSummarization
        })
      });

      const data = await response.json();

      if (data.success || response.ok) {
        this.setState({
          originalPrivacySettings: { ...this.state.privacySettings },
          hasPrivacyChanges: false,
          savingPrivacy: false
        });

        // Reload privacy settings from server to ensure UI is in sync
        this.loadPrivacySettings();

        if (this.props.showToast) {
          this.props.showToast({
            title: "Privacy instellingen opgeslagen",
            position: 'top',
            description: "Uw privacy voorkeuren zijn bijgewerkt.",
            status: "success",
            duration: 3000,
            isClosable: true
          });
        }
      } else {
        throw new Error(data.message || 'Unknown server error');
      }

    } catch (error) {
      console.error('Error saving privacy settings:', error);
      this.setState({ savingPrivacy: false });

      if (this.props.showToast) {
        this.props.showToast({
          title: "Fout bij opslaan",
          position: 'top',
          description: error.message || "Er is een fout opgetreden bij het opslaan van de privacy instellingen.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    }
  }

  handleExportData = async (format = 'json') => {
    this.setState({ exportLoading: true, exportStatus: 'requesting' });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/privacy/export-data`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: this.props.state.username,
          format: format
        })
      });

      const data = await response.json();

      if (data.success || response.ok) {
        this.setState({ exportStatus: 'completed' });

        if (this.props.showToast) {
          this.props.showToast({
            title: "Data export gereed",
            position: 'top',
            description: `Uw data wordt gedownload als ${format.toUpperCase()}...`,
            status: "success",
            duration: 3000,
            isClosable: true
          });
        }

        // Download the file using fetch to maintain session
        if (data.download_url) {
          const downloadResponse = await this.props.authenticatedFetch(`${API_URL}${data.download_url}`, {
            method: 'GET'
          });

          if (downloadResponse.ok) {
            const blob = await downloadResponse.blob();
            const url = window.URL.createObjectURL(blob);
            const link = document.createElement('a');
            link.href = url;
            // CSV exports are actually ZIP files containing multiple CSVs
            const fileExtension = format === 'csv' ? 'zip' : format;
            link.download = `demos_data_export.${fileExtension}`;
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            window.URL.revokeObjectURL(url);
          }
        }
      } else {
        throw new Error(data.message || 'Export failed');
      }

    } catch (error) {
      console.error('Error exporting data:', error);
      this.setState({ exportStatus: 'failed' });

      if (this.props.showToast) {
        this.props.showToast({
          title: "Fout bij data export",
          position: 'top',
          description: error.message || "Er is een fout opgetreden bij het exporteren van uw data.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    } finally {
      this.setState({ exportLoading: false });
      setTimeout(() => this.setState({ exportStatus: null }), 5000);
    }
  }

  handleDeleteAccount = async () => {
    if (this.state.deleteConfirmation !== 'VERWIJDER') {
      if (this.props.showToast) {
        this.props.showToast({
          title: "Bevestiging vereist",
          position: 'top',
          description: "Type 'VERWIJDER' om uw account te verwijderen.",
          status: "warning",
          duration: 3000,
          isClosable: true
        });
      }
      return;
    }

    this.setState({ deletingAccount: true });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/privacy/delete-account`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          username: this.props.state.username,
          reason: 'User requested account deletion'
        })
      });

      const data = await response.json();

      if (data.success || response.ok) {
        if (this.props.showToast) {
          this.props.showToast({
            title: "Account verwijdering aangevraagd",
            position: 'top',
            description: "Uw account zal binnen 30 dagen worden verwijderd. U ontvangt een bevestiging per e-mail.",
            status: "success",
            duration: 8000,
            isClosable: true
          });
        }

        // Close modal and redirect to login after 3 seconds
        this.setState({ showDeleteModal: false, deletingAccount: false });
        setTimeout(() => {
          if (this.props.onLogout) {
            this.props.onLogout();
          }
          this.props.onRouteChange('login');
        }, 3000);
      } else {
        throw new Error(data.message || 'Deletion request failed');
      }

    } catch (error) {
      console.error('Error deleting account:', error);
      this.setState({ deletingAccount: false });

      if (this.props.showToast) {
        this.props.showToast({
          title: "Fout bij verwijdering",
          position: 'top',
          description: error.message || "Er is een fout opgetreden bij het aanvragen van account verwijdering.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    }
  }

  handlePasswordChange = async () => {
    const { currentPassword, newPassword, confirmPassword } = this.state;

    // Validation
    if (!currentPassword || !newPassword || !confirmPassword) {
      if (this.props.showToast) {
        this.props.showToast({
          title: "Velden vereist",
          position: 'top',
          description: "Vul alle velden in om uw wachtwoord te wijzigen.",
          status: "warning",
          duration: 3000,
          isClosable: true
        });
      }
      return;
    }

    if (newPassword !== confirmPassword) {
      if (this.props.showToast) {
        this.props.showToast({
          title: "Wachtwoorden komen niet overeen",
          position: 'top',
          description: "Het nieuwe wachtwoord en bevestiging moeten hetzelfde zijn.",
          status: "error",
          duration: 3000,
          isClosable: true
        });
      }
      return;
    }

    if (newPassword.length < 8) {
      if (this.props.showToast) {
        this.props.showToast({
          title: "Wachtwoord te kort",
          position: 'top',
          description: "Het nieuwe wachtwoord moet minimaal 8 karakters bevatten.",
          status: "error",
          duration: 3000,
          isClosable: true
        });
      }
      return;
    }

    this.setState({ changingPassword: true });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/account/change-password`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          username: this.props.state.username,
          current_password: currentPassword,
          new_password: newPassword
        })
      });

      const data = await response.json();

      if (data.success) {
        this.setState({
          currentPassword: '',
          newPassword: '',
          confirmPassword: '',
          changingPassword: false
        });

        if (this.props.showToast) {
          this.props.showToast({
            title: "Wachtwoord gewijzigd",
            position: 'top',
            description: "Uw wachtwoord is succesvol gewijzigd.",
            status: "success",
            duration: 3000,
            isClosable: true
          });
        }
      } else {
        throw new Error(data.message || 'Password change failed');
      }

    } catch (error) {
      console.error('Error changing password:', error);
      this.setState({ changingPassword: false });

      if (this.props.showToast) {
        this.props.showToast({
          title: "Fout bij wachtwoord wijziging",
          position: 'top',
          description: error.message || "Er is een fout opgetreden bij het wijzigen van uw wachtwoord.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    }
  }

  handleEmailChange = async () => {
    const { newEmail } = this.state;

    // Validation
    if (!newEmail || newEmail.trim() === '') {
      if (this.props.showToast) {
        this.props.showToast({
          title: "E-mailadres vereist",
          position: 'top',
          description: "Vul een nieuw e-mailadres in.",
          status: "warning",
          duration: 3000,
          isClosable: true
        });
      }
      return;
    }

    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(newEmail)) {
      if (this.props.showToast) {
        this.props.showToast({
          title: "Ongeldig e-mailadres",
          position: 'top',
          description: "Vul een geldig e-mailadres in.",
          status: "error",
          duration: 3000,
          isClosable: true
        });
      }
      return;
    }

    this.setState({ changingEmail: true });

    try {
      const response = await this.props.authenticatedFetch(`${API_URL}/api/account/change-email`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          new_email: newEmail.trim().toLowerCase()
        })
      });

      const data = await response.json();

      if (data.success) {
        this.setState({
          newEmail: '',
          changingEmail: false
        });

        // Update parent state with new email
        if (this.props.onEmailChange) {
          this.props.onEmailChange(newEmail.trim().toLowerCase());
        }

        if (this.props.showToast) {
          this.props.showToast({
            title: "E-mailadres gewijzigd",
            position: 'top',
            description: "Uw e-mailadres is succesvol gewijzigd.",
            status: "success",
            duration: 3000,
            isClosable: true
          });
        }
      } else {
        throw new Error(data.message || 'Email change failed');
      }

    } catch (error) {
      console.error('Error changing email:', error);
      this.setState({ changingEmail: false });

      if (this.props.showToast) {
        this.props.showToast({
          title: "Fout bij e-mail wijziging",
          position: 'top',
          description: error.message || "Er is een fout opgetreden bij het wijzigen van uw e-mailadres.",
          status: "error",
          duration: 5000,
          isClosable: true
        });
      }
    }
  }

  handleOpenMobileMenu = () => {
    this.setState({ isMobileMenuOpen: true })
  }

  handleCloseMobileMenu = () => {
    this.setState({ isMobileMenuOpen: false })
  }

  render() {
    const {
      settings,
      hasChanges,
      saving,
      privacySettings,
      hasPrivacyChanges,
      savingPrivacy,
      exportLoading,
      exportStatus,
      showDeleteModal,
      deleteConfirmation,
      deletingAccount,
      currentPassword,
      newPassword,
      confirmPassword,
      changingPassword,
      newEmail,
      changingEmail
    } = this.state;

    const isBurger = this.props.state.userVersion === 'burger';

    return (
      <>
        <Grid templateColumns="repeat(24, 1fr)" bg="gray.50">
          <GridItem
            colSpan={{ base: 0, md: 4, lg: 3 }}
            display={{ base: "none", md: "block" }}
          >
            <Sidebar
              getFeed={this.props.getFeed}
              onRouteChange={this.props.onRouteChange}
              userVersion={this.props.state.userVersion}
              resetSuggestionsPage={this.props.resetSuggestionsPage}
              onLogout={this.props.onLogout}
            />
          </GridItem>

          <GridItem
            colSpan={{ base: 24, md: 20, lg: 21 }}
            display="flex"
            flexDirection="column"
            minHeight="100vh"
          >
            <Navigation
              state={this.props.state}
              userVersion={this.props.userVersion}
              onVersionChange={this.props.onVersionChange}
              username={this.props.username}
              onLogout={this.props.onLogout}
              onOpenFeedback={this.props.onOpenFeedback}
        onOpenMobileMenu = {this.handleOpenMobileMenu}
            />

            <Box p={{ base: 4, md: 8 }} pl={{ base: 4, md: 12 }} pr={{ base: 4, md: 12 }} flex="1">
              {/* Header */}
              <HStack spacing={4} mb={8}>
                <Box
                  p={3}
                  borderRadius="lg"
                >
                  <SettingsIcon boxSize={6} color="gray.600" />
                </Box>
                <Box>
                  <Heading size="lg" color="gray.800" mb={2}>
                    Instellingen
                  </Heading>
                  <Text color="gray.600">
                    {isBurger
                      ? 'Beheer uw privacy voorkeuren en accountgegevens'
                      : 'Configureer wanneer issues als trending worden beschouwd'
                    }
                  </Text>
                </Box>
              </HStack>

              {/* Settings Cards */}
              <VStack spacing={6} align="stretch">

                {/* Account Information Card - Both user types */}
                <Card bg="white" shadow="lg" borderRadius="xl" border="1px solid" borderColor="gray.100">
                  <CardHeader pb={3}>
                    <Heading size="md" color="gray.800">
                      Account Informatie
                    </Heading>
                    <Text fontSize="sm" color="gray.600" mt={1}>
                      Bekijk en beheer uw accountgegevens
                    </Text>
                  </CardHeader>
                  <CardBody pt={0}>
                    <VStack spacing={6} align="stretch">
                      {/* Username Display */}
                      <FormControl>
                        <FormLabel color="gray.700" fontWeight="600">
                          Gebruikersnaam
                        </FormLabel>
                        <Input
                          value={this.props.state.username || ''}
                          isReadOnly
                          bg="neutral.50"
                          size="md"
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Uw unieke gebruikersnaam
                        </Text>
                      </FormControl>

                      <Divider />

                      {/* Email Display and Change */}
                      <FormControl>
                        <FormLabel color="gray.700" fontWeight="600">
                          Huidig e-mailadres
                        </FormLabel>
                        <Input
                          value={this.props.state.email || ''}
                          isReadOnly
                          bg="neutral.50"
                          size="md"
                        />
                        <Text fontSize="xs" color="gray.500" mt={1}>
                          Uw geregistreerde e-mailadres
                        </Text>
                      </FormControl>

                      <FormControl>
                        <FormLabel fontSize="sm" color="gray.700">
                          Nieuw e-mailadres
                        </FormLabel>
                        <Input
                          type="email"
                          placeholder="nieuw@email.com"
                          value={newEmail}
                          onChange={(e) => this.setState({ newEmail: e.target.value })}
                          bg="white"
                          size="md"
                        />
                      </FormControl>

                      <HStack justify="flex-end">
                        <Button
                          leftIcon={<CheckIcon />}
                          colorScheme="gray"
                          onClick={this.handleEmailChange}
                          isLoading={changingEmail}
                          loadingText="Wijzigen..."
                          isDisabled={!newEmail}
                          size="md"
                        >
                          E-mailadres Wijzigen
                        </Button>
                      </HStack>

                      <Divider />

                      {/* Password Change Section */}
                      <Box>
                        <HStack mb={3}>
                          <LockIcon color="gray.500" />
                          <Heading size="sm" color="gray.800">
                            Wachtwoord wijzigen
                          </Heading>
                        </HStack>

                        <VStack spacing={4} align="stretch">
                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700">
                              Huidig wachtwoord
                            </FormLabel>
                            <Input
                              type="password"
                              placeholder="Voer uw huidige wachtwoord in"
                              value={currentPassword}
                              onChange={(e) => this.setState({ currentPassword: e.target.value })}
                              bg="white"
                              size="md"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700">
                              Nieuw wachtwoord
                            </FormLabel>
                            <Input
                              type="password"
                              placeholder="Minimaal 8 karakters"
                              value={newPassword}
                              onChange={(e) => this.setState({ newPassword: e.target.value })}
                              bg="white"
                              size="md"
                            />
                          </FormControl>

                          <FormControl>
                            <FormLabel fontSize="sm" color="gray.700">
                              Bevestig nieuw wachtwoord
                            </FormLabel>
                            <Input
                              type="password"
                              placeholder="Herhaal uw nieuwe wachtwoord"
                              value={confirmPassword}
                              onChange={(e) => this.setState({ confirmPassword: e.target.value })}
                              bg="white"
                              size="md"
                            />
                          </FormControl>

                          <HStack justify="flex-end" pt={2}>
                            <Button
                              leftIcon={<CheckIcon />}
                              colorScheme="gray"
                              onClick={this.handlePasswordChange}
                              isLoading={changingPassword}
                              loadingText="Wijzigen..."
                              isDisabled={!currentPassword || !newPassword || !confirmPassword}
                              size="md"
                            >
                              Wachtwoord Wijzigen
                            </Button>
                          </HStack>
                        </VStack>
                      </Box>
                    </VStack>
                  </CardBody>
                </Card>

                {/* Privacy Settings for Burger users only */}
                {isBurger && (
                  <>

                    {/* AI Summarization Setting */}
                    <Card bg="white" shadow="lg" borderRadius="xl" border="1px solid" borderColor="gray.100">
                      <CardHeader pb={3}>
                        <Heading size="md" color="gray.800">
                          AI Verwerking
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          Beheer hoe uw suggesties worden verwerkt
                        </Text>
                      </CardHeader>
                      <CardBody pt={0}>
                        <FormControl display="flex" flexDirection={{ base: "column", md: "row" }} alignItems={{ base: "flex-start", md: "center" }} justifyContent="space-between">
                          <Box flex="1" mr={{ base: 0, md: 4 }} mb={{ base: 3, md: 0 }}>
                            <FormLabel htmlFor="ai-summarization" mb={1} color="gray.700" fontWeight="600">
                              Suggesties gebruiken voor AI samenvattingen
                            </FormLabel>
                            <Text fontSize="sm" color="gray.600">
                              Sta toe dat uw suggesties worden gebruikt voor het genereren van trending issues en samenvattingen voor vertegenwoordigers.
                            </Text>
                          </Box>
                          <Switch
                            id="ai-summarization"
                            size="lg"
                            colorScheme="red"
                            isChecked={privacySettings.allowSuggestionSummarization}
                            onChange={(e) => this.handlePrivacySettingChange('allowSuggestionSummarization', e.target.checked)}
                          />
                        </FormControl>

                        {hasPrivacyChanges && (
                          <HStack spacing={4} justify="flex-end" pt={6} mt={6} borderTop="1px solid" borderColor="gray.100">
                            <Button
                              leftIcon={<CheckIcon />}
                              bg={colors.primary.main}
                              color="white"
                              _hover={{ bg: colors.primary.dark }}
                              onClick={this.savePrivacySettings}
                              isLoading={savingPrivacy}
                              loadingText="Opslaan..."
                              size="md"
                            >
                              Privacy Instellingen Opslaan
                            </Button>
                          </HStack>
                        )}
                      </CardBody>
                    </Card>

                    {/* Data Export Card - GDPR Article 15 & 20 */}
                    <Card bg="white" shadow="lg" borderRadius="xl" border="1px solid" borderColor="gray.100">
                      <CardHeader pb={3}>
                        <Heading size="md" color="gray.800">
                          Data Exporteren
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          Download een kopie van al uw gegevens
                        </Text>
                      </CardHeader>
                      <CardBody pt={0}>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          U heeft het recht om een kopie van al uw persoonlijke gegevens te ontvangen in een gestructureerd, algemeen gebruikt en machine-leesbaar formaat.
                        </Text>
                        <Text fontSize="sm" color="gray.600" mb={4}>
                          Uw export bevat: accountgegevens, suggesties, berichten, privacyinstellingen en toestemmingen.
                        </Text>

                        <HStack spacing={3}>
                          <Button
                            leftIcon={exportLoading ? <Spinner size="sm" /> : <DownloadIcon />}
                            colorScheme="gray"
                            onClick={() => this.handleExportData('json')}
                            isLoading={exportLoading}
                            loadingText="Voorbereiden..."
                            isDisabled={exportLoading}
                            size="md"
                          >
                            JSON
                          </Button>
                          <Button
                            leftIcon={exportLoading ? <Spinner size="sm" /> : <DownloadIcon />}
                            colorScheme="gray"
                            onClick={() => this.handleExportData('csv')}
                            isLoading={exportLoading}
                            loadingText="Voorbereiden..."
                            isDisabled={exportLoading}
                            size="md"
                          >
                            CSV (Excel)
                          </Button>
                        </HStack>

                        {exportStatus === 'completed' && (
                          <Alert status="success" mt={4} borderRadius="md">
                            <AlertIcon />
                            <Box>
                              <AlertTitle fontSize="sm">Export gereed!</AlertTitle>
                              <AlertDescription fontSize="sm">
                                Uw data wordt gedownload.
                              </AlertDescription>
                            </Box>
                          </Alert>
                        )}

                        {exportStatus === 'failed' && (
                          <Alert status="error" mt={4} borderRadius="md">
                            <AlertIcon />
                            <Box>
                              <AlertTitle fontSize="sm">Export mislukt</AlertTitle>
                              <AlertDescription fontSize="sm">
                                Er is een fout opgetreden. Probeer het later opnieuw.
                              </AlertDescription>
                            </Box>
                          </Alert>
                        )}
                      </CardBody>
                    </Card>

                    {/* Account Deletion Card - GDPR Article 17 */}
                    <Card bg="white" shadow="lg" borderRadius="xl" border="1px solid" borderColor="red.200">
                      <CardHeader pb={3}>
                        <HStack>
                          <WarningIcon color="red.500" />
                          <Heading size="md" color="red.600">
                            Account Verwijderen
                          </Heading>
                        </HStack>  
                      </CardHeader>
                      <CardBody pt={0}>
                        <Text fontSize="sm" color="gray.600" mb={2}>
                          U heeft het recht om uw persoonsgegevens te laten verwijderen. Dit omvat:
                        </Text>
                        <VStack align="start" spacing={1} mb={4} pl={4}>
                          <Text fontSize="sm" color="gray.600">• Uw accountgegevens (email, gebruikersnaam)</Text>
                          <Text fontSize="sm" color="gray.600">• Al uw ingediende suggesties</Text>
                          <Text fontSize="sm" color="gray.600">• Al uw berichten</Text>
                          <Text fontSize="sm" color="gray.600">• Uw privacy instellingen</Text>
                        </VStack>

                        <Alert status="warning" mb={4} borderRadius="md">
                          <AlertIcon />
                          <Box>
                            <AlertTitle fontSize="sm">Let op: Grace periode van 30 dagen</AlertTitle>
                            <AlertDescription fontSize="sm">
                              Uw account wordt gemarkeerd voor verwijdering en na 30 dagen permanent verwijderd. U kunt dit binnen 30 dagen annuleren door contact op te nemen met privacy@metdemos.app.
                            </AlertDescription>
                          </Box>
                        </Alert>

                        <Button
                          leftIcon={<DeleteIcon />}
                          colorScheme="red"
                          variant="outline"
                          onClick={() => this.setState({ showDeleteModal: true })}
                          size="lg"
                        >
                          Account Verwijderen
                        </Button>
                      </CardBody>
                    </Card>
                  </>
                )}

                {/* Trending Issues Settings Card - Only for Vertegenwoordiger */}
                {!isBurger && (
                  <>
                    <Card bg="white" shadow="lg" borderRadius="xl" border="1px solid" borderColor="gray.100">
                      <CardHeader pb={3}>
                        <Heading size="md" color="gray.800">
                          📈 Trending Issues Drempels
                        </Heading>
                        <Text fontSize="sm" color="gray.600" mt={1}>
                          Stel in wanneer een issue als trending wordt beschouwd
                        </Text>
                      </CardHeader>
                      <CardBody pt={0}>
                        <Stack spacing={6}>

                          <FormControl>
                            <FormLabel color="gray.700" fontWeight="600">
                              Minimum aantal personen
                            </FormLabel>
                            <Text fontSize="sm" color="gray.600" mb={3}>
                              Het minimum aantal verschillende personen dat over hetzelfde onderwerp moet spreken
                            </Text>
                            <NumberInput
                              value={settings.minimumPersonsForTrending}
                              min={1}
                              max={20}
                              onChange={(_, valueNumber) =>
                                this.handleSettingChange('minimumPersonsForTrending', valueNumber)
                              }
                              size="lg"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                          <Divider />

                          <FormControl>
                            <FormLabel color="gray.700" fontWeight="600">
                              Minimum aantal suggesties
                            </FormLabel>
                            <Text fontSize="sm" color="gray.600" mb={3}>
                              Het minimum aantal suggesties dat nodig is voor een trending issue
                            </Text>
                            <NumberInput
                              value={settings.minimumSuggestionsForTrending}
                              min={1}
                              max={50}
                              onChange={(_, valueNumber) =>
                                this.handleSettingChange('minimumSuggestionsForTrending', valueNumber)
                              }
                              size="lg"
                            >
                              <NumberInputField />
                              <NumberInputStepper>
                                <NumberIncrementStepper />
                                <NumberDecrementStepper />
                              </NumberInputStepper>
                            </NumberInput>
                          </FormControl>

                        </Stack>
                      </CardBody>
                    </Card>

                    {/* Info Card */}
                    <Alert status="warning" borderRadius="lg" bg="orange.50" borderColor="orange.200" border="1px solid">
                      <AlertIcon color="orange.500" />
                      <Box>
                        <AlertTitle color="orange.700" fontSize="sm">
                          Let op!
                        </AlertTitle>
                        <AlertDescription color="orange.700" fontSize="sm">
                          Wijzigingen worden toegepast bij het volgende laden van trending issues.
                          Issues die al trending zijn blijven zichtbaar tot ze worden vernieuwd.
                        </AlertDescription>
                      </Box>
                    </Alert>

                    {/* Action Buttons */}
                    <Stack
                      direction={{ base: "column", md: "row" }}
                      spacing={3}
                      align="stretch"
                      pt={4}
                    >
                      <Button
                        leftIcon={<CheckIcon />}
                        bg={colors.primary.main}
                        color="white"
                        _hover={{ bg: colors.primary.dark }}
                        onClick={this.saveSettings}
                        isDisabled={!hasChanges}
                        isLoading={saving}
                        loadingText="Opslaan..."
                        size="md"
                        flex={{ base: "1", md: "1" }}
                        minH={{ base: "48px", md: "auto" }}
                      >
                        Instellingen Opslaan
                      </Button>

                      <Button
                        variant="outline"
                        colorScheme="gray"
                        onClick={this.resetSettings}
                        size="md"
                        flex={{ base: "1", md: "1" }}
                        minH={{ base: "48px", md: "auto" }}
                      >
                        Standaardwaarden
                      </Button>
                    </Stack>
                  </>
                )}

              </VStack>
            </Box>
        <Footer onRouteChange={this.props.onRouteChange} />

          </GridItem>
        </Grid>

        {/* Delete Account Confirmation Modal */}
        <Modal isOpen={showDeleteModal} onClose={() => this.setState({ showDeleteModal: false, deleteConfirmation: '' })}>
          <ModalOverlay />
          <ModalContent>
            <ModalHeader>
              <HStack>
                <WarningIcon color="red.500" />
                <Text>Account Verwijderen</Text>
              </HStack>
            </ModalHeader>
            <ModalCloseButton />
            <ModalBody>
              <VStack spacing={4} align="stretch">
                <Alert status="error" borderRadius="md">
                  <AlertIcon />
                  <Box>
                    <AlertTitle fontSize="sm">Let op!</AlertTitle>
                    <AlertDescription fontSize="sm">
                      Deze actie is permanent en kan niet ongedaan worden gemaakt.
                    </AlertDescription>
                  </Box>
                </Alert>

                <Text fontSize="sm" color="gray.700">
                  Alle volgende gegevens zullen permanent worden verwijderd:
                </Text>
                <Box as="ul" pl={6} fontSize="sm" color="gray.600">
                  <li>Uw account en profiel</li>
                  <li>Alle ingediende suggesties</li>
                  <li>Berichten en communicatie</li>
                </Box>

                <Text fontSize="sm" color="gray.700" fontWeight="600" mt={4}>
                  Type <Badge colorScheme="red">VERWIJDER</Badge> om te bevestigen:
                </Text>
                <Input
                  placeholder="Type VERWIJDER"
                  value={deleteConfirmation}
                  onChange={(e) => this.setState({ deleteConfirmation: e.target.value })}
                  bg="white"
                  size="lg"
                />
              </VStack>
            </ModalBody>

            <ModalFooter>
              <Button variant="ghost" mr={3} onClick={() => this.setState({ showDeleteModal: false, deleteConfirmation: '' })}>
                Annuleren
              </Button>
              <Button
                colorScheme="red"
                onClick={this.handleDeleteAccount}
                isDisabled={deleteConfirmation !== 'VERWIJDER'}
                isLoading={deletingAccount}
                loadingText="Verwijderen..."
              >
                Verwijder Account
              </Button>
            </ModalFooter>
          </ModalContent>
        </Modal>
      
        {/* Mobile Menu Drawer */}
        <MobileMenu
          isOpen={this.state.isMobileMenuOpen}
          onClose={this.handleCloseMobileMenu}
          onRouteChange={this.props.onRouteChange}
          userVersion={this.props.state.userVersion}
          resetSuggestionsPage={this.props.resetSuggestionsPage}
          onLogout={this.props.onLogout}
        />
      </>
    )
  }
}

export default Instellingen